import { historyFailed, historyPaymentAlerts, isPaid, sessionAlerts, sessionPaymentAlerts } from "./alerts";
import format from "./format";

// Formato comum para sessão e histórico, assim a lista unificada e o ChargeRow não dependem de qual API o dado veio

function fromSession(session) {
    const alerts = sessionAlerts(session);
    const paymentAlerts = sessionPaymentAlerts(session);
    return {
        key: session.transactionId ? `tx-${session.transactionId}` : `s-${session.id}`,
        kind: "session",
        transactionId: session.transactionId,
        start: session.startConnected,
        charger: session.charger,
        plug: session.plug,
        user: session.user,
        nfc: session.nfc,
        unlockBy: session.unlockBy,
        status: session.status,
        consumption: session.consumption,
        power: session.power,
        maximumPower: session.maximumPower,
        minutesCharger: session.minutesCharger,
        minutesConnected: session.minutesConnected,
        percentStart: session.percentStart,
        percent: session.percent,
        alerts,
        paid: isPaid(session),
        payment: session.payment,
        paymentAlerts,
        alert: alerts.any || paymentAlerts.length > 0,
        sessionError: alerts.error,
        critical: alerts.error || paymentAlerts.some((a) => a.critical),
        inconsistentHistory: null,
    };
}

function fromHistory(item, key) {
    const car = item.car || {};
    const failed = historyFailed(item);
    const paymentAlerts = historyPaymentAlerts(item);
    return {
        key,
        kind: "history",
        transactionId: item.transactionId,
        start: item.dateTime,
        charger: item.charger,
        plug: item.plug,
        user: item.user,
        nfc: car.nfc,
        unlockBy: null,
        status: item.status,
        consumption: car.consumption,
        power: null,
        maximumPower: car.maximumPower,
        minutesCharger: car.minutesCharger,
        minutesConnected: car.minutesConnected,
        percentStart: car.percentStart,
        percent: car.percent,
        failed,
        paid: isPaid(item),
        payment: item.payment,
        paymentAlerts,
        alert: failed || paymentAlerts.length > 0,
        sessionError: false,
        critical: paymentAlerts.some((a) => a.critical),
        inconsistentHistory: null,
        // Original para gerar o recibo
        history: item,
    };
}

function compare(a, b) {
    // Críticos no topo, depois do início mais recente para o mais antigo, sem data no final
    if (a.critical != b.critical) return a.critical ? -1 : 1;
    const startA = format.sortableDateTime(a.start);
    const startB = format.sortableDateTime(b.start);
    if (startA != startB) {
        if (!startA) return 1;
        if (!startB) return -1;
        return startA < startB ? 1 : -1;
    }
    return (b.transactionId || 0) - (a.transactionId || 0);
}

/**
 * Junta sessões e históricos numa única lista usando o transaction_id como identidade,
 * assim a linha da sessão é reaproveitada quando ela vira histórico.
 *
 * Sessão e histórico com o mesmo transaction_id só é inconsistência se as sessões foram buscadas
 * depois do histórico: o backend grava o histórico antes de limpar a sessão, então com a ordem
 * inversa é apenas a transição normal acontecendo entre as duas buscas.
 */
export function buildCharges(sessions, history, { sessionsRequestedAt, historyRequestedAt }) {
    const sessionsAfterHistory = sessionsRequestedAt && historyRequestedAt && sessionsRequestedAt > historyRequestedAt;
    const usedKeys = new Set();
    const uniqueKey = (key, fallback) => (usedKeys.has(key) ? fallback : key);

    const historyByTransaction = new Map();
    history.forEach((item) => {
        if (item.transactionId && !historyByTransaction.has(item.transactionId)) {
            historyByTransaction.set(item.transactionId, item);
        }
    });

    const items = [];
    const mergedHistory = new Set();

    sessions.forEach((session) => {
        const historyItem = session.transactionId ? historyByTransaction.get(session.transactionId) : null;
        let item;
        if (!historyItem) {
            item = fromSession(session);
        } else if (sessionsAfterHistory) {
            item = { ...fromSession(session), inconsistentHistory: fromHistory(historyItem, null), alert: true, critical: true };
            mergedHistory.add(historyItem);
        } else {
            item = fromHistory(historyItem, `tx-${session.transactionId}`);
            mergedHistory.add(historyItem);
        }
        item.key = uniqueKey(item.key, `s-${session.id}`);
        usedKeys.add(item.key);
        items.push(item);
    });

    history.forEach((historyItem) => {
        if (mergedHistory.has(historyItem)) return;
        // O backend pode gravar o mesmo histórico mais de uma vez, então a chave precisa continuar única
        const key = uniqueKey(historyItem.transactionId ? `tx-${historyItem.transactionId}` : `h-${historyItem.id}`, `h-${historyItem.id}`);
        usedKeys.add(key);
        items.push(fromHistory(historyItem, key));
    });

    return items.sort(compare);
}
